import { AI_SERVICE_CONFIG } from '@/config/aiConfig';

export interface DashboardContext {
  timezone: string;
  offsetInMinutes?: number;
  queueIds?: string[];
  agentIds?: string[];
  directAgentIds?: string[];
  activeTab?: string;
  allQueuesSelected?: boolean;
  allAgentsSelected?: boolean;
  allDirectAgentsSelected?: boolean;
}

export interface EvidenceItem {
  evidenceId: string;
  sourceType: string;
  title: string;
  value: string;
  unit?: string;
  sourceReference: string;
  metricKey?: string;
  periodLabel?: string;
}

export interface TelemetryMeta {
  promptCategory: string;
  latencyMs: number;
  resolverLayer: string;
  tokenUsage?: number;
  backendStatus?: number;
  fallbackReason?: string;
}

export interface ResolvedTimeRange {
  sourcePhrase: string;
  rangeKey: string;
  startUtc: string;
  endUtc: string;
  comparisonStartUtc?: string;
  comparisonEndUtc?: string;
}

export interface CreateSessionResponse {
  sessionId: string;
  locale: string;
  created: boolean;
}

export interface ChatMessageResponse {
  sessionId: string;
  turnId: string;
  answer: string;
  responseType: string;
  resolvedTimeRange?: ResolvedTimeRange;
  evidence: EvidenceItem[];
  status: string;
  telemetry?: TelemetryMeta;
}

function getBaseUrl(): string {
  const url = AI_SERVICE_CONFIG.baseUrl;
  if (!url) throw new Error('VITE_AI_SERVICE_URL is not configured');
  return url.replace(/\/$/, '');
}

export async function createSession(
  sessionId: string,
  locale: string,
  timezone: string,
  bearerToken: string,
  dashboardContext?: DashboardContext,
): Promise<CreateSessionResponse> {
  const response = await fetch(`${getBaseUrl()}/api/chat/session`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${bearerToken}`,
    },
    body: JSON.stringify({ sessionId, locale, timezone, dashboardContext }),
  });

  if (!response.ok) {
    throw new Error(`Failed to create session: ${response.status}`);
  }

  return response.json() as Promise<CreateSessionResponse>;
}

export async function sendMessage(
  sessionId: string,
  question: string,
  bearerToken: string,
  dashboardContext?: DashboardContext,
  signal?: AbortSignal,
): Promise<ChatMessageResponse> {
  const response = await fetch(`${getBaseUrl()}/api/chat/message`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${bearerToken}`,
    },
    body: JSON.stringify({ sessionId, question, dashboardContext }),
    signal,
  });

  if (!response.ok) {
    throw new Error(`AI service error: ${response.status}`);
  }

  return response.json() as Promise<ChatMessageResponse>;
}

export type StreamFrame =
  | { type: 'meta'; sessionId: string; turnId: string }
  | { type: 'delta'; content: string }
  | { type: 'done'; answer: string; evidence: EvidenceItem[]; telemetry?: TelemetryMeta; status: string };

export async function* streamMessage(
  sessionId: string,
  question: string,
  bearerToken: string,
  dashboardContext?: DashboardContext,
  signal?: AbortSignal,
): AsyncGenerator<StreamFrame> {
  const response = await fetch(`${getBaseUrl()}/api/chat/message/stream`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${bearerToken}`,
    },
    body: JSON.stringify({ sessionId, question, dashboardContext }),
    signal,
  });

  if (!response.ok) {
    throw new Error(`AI service error: ${response.status}`);
  }

  const reader = response.body!.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() ?? '';
      for (const line of lines) {
        const trimmed = line.trim();
        if (!trimmed) continue;
        try {
          yield JSON.parse(trimmed) as StreamFrame;
        } catch {
          // ignore malformed frames
        }
      }
    }
  } finally {
    reader.releaseLock();
  }
}
